// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertyTagNfcsService } from 'src/app/modules/properties/propertyTagNfcs.service';
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Script start
 */

@Component({
  selector: "app-property-tag-nfc-add-dialog",
  templateUrl: "./property-tag-nfc-add-dialog.component.html",
  styleUrls: ["./property-tag-nfc-add-dialog.component.scss"],
})
export class PropertyTagNfcAddDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit phonebook contact form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true,
    },
  };
  // END - Edit phonebook contact form

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<PropertyTagNfcAddDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private propertyTagNfcsService: PropertyTagNfcsService,
    public confirmDialog: MatDialog
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe((res) => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        (res) => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // Init add form
    this.initForm();

    // Stop loading dialog
    this.dialogIsLoading = false;
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    this.model["files"] = [];

    this.fields.push({
      fieldGroupClassName: "row",
      fieldGroup: [
        {
          className: "col-12",
          type: "file-uploader-structure",
          key: "images",
          templateOptions: {
            required: false,
            allowedFilesNumber: 3,
            allowedFilesExtensions: 'jpg,jpeg,png,gif',
            customLabel: this.translateService.instant(
              "propertyTagNfc.fields.images"
            ),
          },
        },
        {
          className: "col-12",
          type: "input",
          key: "uid",
          defaultValue: this.data.uid,
          templateOptions: {
            label: this.translateService.instant("propertyTagNfc.fields.uid"),
            placeholder: this.translateService.instant(
              "propertyTagNfc.fields.uid"
            ),
            required: true,
            minLength: 0,
            maxLength: 1000,
            rows: 4
          },
          wrappers: ['form-field-horizontal']
        },
        {
          className: "col-12",
          type: "input",
          key: "name",
          defaultValue: this.data.name,
          templateOptions: {
            label: this.translateService.instant("propertyTagNfc.fields.name"),
            placeholder: this.translateService.instant(
              "propertyTagNfc.fields.name"
            ),
            required: true,
          },
          wrappers: ['form-field-horizontal']
        },
        {
          className: "col-12",
          type: "select",
          key: "shortDescription",
          defaultValue: this.data.shortDescription,
          templateOptions: {
            label: this.translateService.instant("propertyTagNfc.fields.shortDescription"),
            placeholder: this.translateService.instant(
              "propertyTagNfc.fields.shortDescription"
            ),
            required: false,
            options: [
              {
                value: "externalPushButtonPanel",
                label: this.translateService.instant("propertyTagNfc.shortDescription.externalPushButtonPanel"),
              },
              {
                value: "understairsPushButtonPanel",
                label: this.translateService.instant("propertyTagNfc.shortDescription.understairsPushButtonPanel"),
              },
              {
                value: "dedicatedLocalAccessDoor",
                label: this.translateService.instant("propertyTagNfc.shortDescription.dedicatedLocalAccessDoor"),
              },
              {
                value: "driveway",
                label: this.translateService.instant("propertyTagNfc.shortDescription.driveway"),
              },
              {
                value: "other",
                label: this.translateService.instant("propertyTagNfc.shortDescription.other"),
              },
            ],
          },
          wrappers: ['form-field-horizontal']
        },
        {
          className: "col-12",
          type: "textarea",
          key: "description",
          defaultValue: this.data.description,
          templateOptions: {
            label: this.translateService.instant("propertyTagNfc.fields.description"),
            placeholder: this.translateService.instant(
              "propertyTagNfc.fields.description"
            ),
            required: false,
            minLength: 0,
            maxLength: 1000,
            rows: 4
          },
          wrappers: ['form-field-horizontal']
        },
        {
          className: "col-12",
          type: "input",
          key: "latitude",
          defaultValue: this.data.latitude,
          templateOptions: {
            label: this.translateService.instant("propertyTagNfc.fields.latitude"),
            placeholder: this.translateService.instant(
              "propertyTagNfc.fields.latitude"
            ),
            required: false,
          },
          wrappers: ['form-field-horizontal']
        },
        {
          className: "col-12",
          type: "input",
          key: "longitude",
          defaultValue: this.data.longitude,
          templateOptions: {
            label: this.translateService.instant("propertyTagNfc.fields.longitude"),
            placeholder: this.translateService.instant(
              "propertyTagNfc.fields.longitude"
            ),
            required: false,
          },
          wrappers: ['form-field-horizontal']
        },
        {
          className: "col-12",
          type: "input",
          key: "installationDate",
          defaultValue: this.data.installationDate,
          templateOptions: {
            type: 'date',
            label: this.translateService.instant("propertyTagNfc.fields.installationDate"),
            placeholder: this.translateService.instant(
              "propertyTagNfc.fields.installationDate"
            ),
            required: false,
          },
          wrappers: ['form-field-horizontal']
        },
      ],
    });
  }
  
  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
   onFormSubmit(model: any): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    const sendData = {
      images: model.files,
      uid: model.uid,
      name: model.name,
      shortDescription: model.shortDescription,
      description: model.description,
      latitude: model.latitude,
      longitude: model.longitude,
      installationDate: model.installationDate,
      toActivate: true
    };

    this.subscriptions.push(
      this.propertyTagNfcsService.create(
        this.data.propertyId,
        sendData,
      )
        .pipe(
          finalize(() => this.formBtnOptions.active = false)
        )
        .subscribe(
          res => {
            if (res.status) {
              this.toastrService.success(res.message);
              this.onClose(res);
              return;
            }
            this.toastrService.error(this.translateService.instant('common.errors.unknown_server_error'));
          },
          err => {
            // Choose one of the following error handling method.
            // 1) First one show a message right under the form fields
            //    (if the form is properly setted)
            // 2) Second one is used to show a mat-error under each field
            //    in the dynamic form (is meant to be used only in dynamicForm)
            // 3) Third one show toastr notifications for each error
            // 4) Fourth one is used to show mat-error under each field
            //    in the ngx-formly form (is mean to be used only with ngx-formly)
            // this.helperService.handleFormError(form, err);
            // this.helperService.handleDynamicFormError(form, err);
            // this.helperService.handleError(err);
            this.helperService.handleFormlyError(this.form, err);
          }
        )
    );
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   *
   * @param [data] optional data passed to the caller component
   */
   onClose(data?: any): void {
    this.dialogRef.close(data);
  }
}
